import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Building2, TrendingUp, Shield, Users, Zap, ChevronRight, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: "lender",        label: "Lender / Investor",  desc: "I provide capital for acquisitions",   color: "#0A84FF", icon: TrendingUp },
  { value: "realtor",       label: "Realtor / Agent",     desc: "I handle listings and buyer rep",     color: "#00FF88", icon: Building2 },
  { value: "contractor",    label: "Contractor / GC",     desc: "I manage rehab and construction",      color: "#FF9F0A", icon: Zap },
  { value: "title",         label: "Title / Settlement",  desc: "I facilitate closings and transfers",  color: "#AF52DE", icon: Shield },
  { value: "investor",      label: "Investor / Operator", desc: "I acquire and manage properties",      color: "#D4AF37", icon: Users },
];

export default function InviteLandingPage() {
  const { deal_id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const presetRole  = searchParams.get("role") || "";
  const presetEmail = searchParams.get("email") || "";

  const [deal, setDeal]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole]       = useState(presetRole);

  useEffect(() => {
    if (!deal_id) return;
    fetch(`${API}/api/deals/public/preview/${deal_id}`)
      .then(r => r.json())
      .then(setDeal)
      .catch(() => setDeal({ teaser_only: true, message: "You've been invited to collaborate." }))
      .finally(() => setLoading(false));
  }, [deal_id]);

  const handleJoin = () => {
    const params = new URLSearchParams();
    if (role) params.set("role", role);
    if (presetEmail) params.set("email", presetEmail);
    params.set("invite", deal_id);
    navigate(`/register?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isTeaser = deal?.teaser_only;

  return (
    <div className="min-h-screen bg-[#09090B] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-white/[0.06] px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <div className="w-8 h-8 bg-[#D4AF37] rounded-lg flex items-center justify-center">
            <span className="text-black font-bold text-xs">DF</span>
          </div>
          <span className="text-white font-semibold">DealForge</span>
          <span className="text-white/20 text-sm ml-1">— Real Estate Transaction Platform</span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl space-y-8">

          {/* Deal Card */}
          <div className="bg-[#0D0D0D] border border-[#D4AF37]/20 rounded-2xl p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-[10px] text-[#D4AF37] font-bold uppercase tracking-widest mb-0.5">Transaction Collaboration Invite</p>
                <h1 className="text-xl sm:text-2xl font-bold text-white">
                  {isTeaser ? "You've been invited to collaborate" : deal?.address || "Transaction Opportunity"}
                </h1>
              </div>
            </div>

            {!isTeaser && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                {deal?.price && (
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 text-center">
                    <p className="text-[10px] text-white/30 uppercase mb-1">Asking</p>
                    <p className="text-[#D4AF37] font-bold font-mono">${(deal.price / 1000).toFixed(0)}K</p>
                  </div>
                )}
                {deal?.arv && (
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 text-center">
                    <p className="text-[10px] text-white/30 uppercase mb-1">ARV</p>
                    <p className="text-[#00FF88] font-bold font-mono">${(deal.arv / 1000).toFixed(0)}K</p>
                  </div>
                )}
                {deal?.deal_score && (
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 text-center">
                    <p className="text-[10px] text-white/30 uppercase mb-1">Score</p>
                    <p className="text-white font-bold">{deal.deal_score}/100</p>
                  </div>
                )}
                {deal?.deal_type && (
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 text-center">
                    <p className="text-[10px] text-white/30 uppercase mb-1">Type</p>
                    <p className="text-white/70 text-xs font-semibold capitalize">{deal.deal_type}</p>
                  </div>
                )}
              </div>
            )}

            {isTeaser && (
              <p className="text-white/50 text-sm">{deal?.message}</p>
            )}
          </div>

          {/* Role Selector */}
          <div className="space-y-3">
            <h2 className="text-white font-semibold">I'm joining as a…</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ROLE_OPTIONS.map(r => {
                const Icon = r.icon;
                return (
                  <button
                    key={r.value}
                    onClick={() => setRole(r.value)}
                    data-testid={`role-select-${r.value}`}
                    className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${
                      role === r.value
                        ? "border-[#D4AF37]/40 bg-[#D4AF37]/[0.06]"
                        : "border-white/[0.06] hover:border-white/10 bg-white/[0.02]"
                    }`}
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${r.color}15`, color: r.color }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${role === r.value ? "text-[#D4AF37]" : "text-white"}`}>
                        {r.label}
                      </p>
                      <p className="text-[11px] text-white/30">{r.desc}</p>
                    </div>
                    {role === r.value && <CheckCircle className="w-4 h-4 text-[#D4AF37] ml-auto flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <div className="space-y-3">
            <button
              onClick={handleJoin}
              disabled={!role}
              data-testid="invite-join-btn"
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D4AF37] hover:bg-[#D4AF37]/90 disabled:opacity-40 text-black font-bold rounded-xl text-base transition-all"
            >
              Join as {role ? ROLE_OPTIONS.find(r => r.value === role)?.label : "…"} on DealForge
              <ChevronRight className="w-5 h-5" />
            </button>
            <p className="text-center text-[11px] text-white/20">
              Free to join. No credit card required.
            </p>
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11px] text-white/20">
            <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Secure transactions</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Stripe-powered payments</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> ATTOM property data</span>
          </div>
        </div>
      </main>
    </div>
  );
}
